import version from '@/utils/version';

export class AnalyticsApi {
  private readonly queue: (() => Promise<Response>)[] = [];

  private readonly FLUSH_INTERVAL = 1000;

  private flushed = false;

  private intervalId: number | NodeJS.Timeout;

  constructor(
    private readonly userId: string,
    private readonly gatewayUrl: string,
  ) {
    this.intervalId = setInterval(() => {
      if (this.queue.length || !this.flushed) {
        this.flush();
      }
    }, this.FLUSH_INTERVAL);
  }

  identify(traits: Record<string, unknown> = {}): void {
    this.queue.push(() =>
      fetch(`${this.gatewayUrl}/analytics/identify`, {
        method: 'POST',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify({
          userId: this.userId,
          traits: {
            // eslint-disable-next-line camelcase
            sdk_version: version,
            source: 'video_sdk',
            ...traits,
          },
        }),
      }),
    );

    this.flushed = false;
  }

  track(event: EVENTS, properties: Record<string, unknown> = {}): void {
    this.queue.push(() =>
      fetch(`${this.gatewayUrl}/analytics/track`, {
        method: 'POST',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify({
          userId: this.userId,
          event,
          properties,
        }),
      }),
    );

    this.flushed = false;
  }

  private flush(): void {
    this.flushed = true;
    while (this.queue.length) {
      const req = this.queue.shift();
      if (req) {
        req().catch(() => {
          return;
        });
      }
    }
  }

  private getDefaultHeaders() {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'User-Agent': `Naluri Video SDK /JS (${version}})`,
      'x-nl-sdk-version': version,
    };
  }

  cleanup(): void {
    this.flush();
    clearInterval(this.intervalId);
  }
}

export const EVENTS = {
  LOGIN: 'login',
  REFRESH: 'refresh',
  LOGOUT: 'logout',
  CONNECT_ROOM: 'connect_room',

  // errors
  LOGIN_ERROR: 'login_error',
  LOGOUT_ERROR: 'logout_error',
  REFRESH_ERROR: 'refresh_error',
  CONNECT_ROOM_ERROR: 'connect_room_error',
} as const;

export type EVENTS = (typeof EVENTS)[keyof typeof EVENTS];
